import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FaLock, FaEye, FaEyeSlash, FaKey, FaShieldAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import API from "../../services/api";

const DefaultPasswordModal = ({ show, onHide, onSuccess, onCancel, userId }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 6) errors.push("At least 6 characters");
    if (!/[0-9]/.test(password)) errors.push("At least one number");
    if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
    return errors;
  };

  const handleCancel = () => {
    onCancel();
    onHide();
    toast.info("You can change your password later from your profile.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!newPassword) {
      setErrors({ new: "New password is required" });
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setErrors({ passwordStrength: passwordErrors });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirm: "Passwords do not match" });
      return;
    }

    try {
      setLoading(true);
      const response = await API.post(`/student/${userId}/change-password`, {
        current_password: "123456",
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (response.data.status) {
        toast.success("Password changed successfully!");
        onSuccess(userId);
        onHide();
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      console.error("Password change error:", error);
      const errorMsg = error.response?.data?.message || "Failed to change password";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const errors = validatePassword(newPassword);
    if (!newPassword) return { level: 0, text: "" };
    if (errors.length === 0) return { level: 3, text: "Strong", color: "#28a745" };
    if (errors.length <= 1) return { level: 2, text: "Medium", color: "#ffc107" };
    return { level: 1, text: "Weak", color: "#dc3545" };
  };

  const strength = getPasswordStrength();

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard={false} size="lg">
      <Modal.Header className="bg-warning text-dark">
        <Modal.Title>
          <FaShieldAlt className="me-2" />
          Change Default Password
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="warning" className="mb-4">
          <FaLock className="me-2" />
          <strong>Security Notice:</strong> You are using the default password (123456). 
          For your account security, please change your password.
        </Alert>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label>New Password *</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                isInvalid={!!errors.new}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {newPassword && (
              <div className="mt-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small>Password Strength:</small>
                  <small style={{ color: strength.color }}>{strength.text}</small>
                </div>
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${(strength.level / 3) * 100}%`,
                      backgroundColor: strength.color,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            )}

            <Form.Text className="text-muted">
              Password must be at least 6 characters, contain at least one number and one uppercase letter.
            </Form.Text>

            {errors.passwordStrength && (
              <div className="mt-2">
                <small className="text-danger">
                  <strong>Requirements:</strong>
                  <ul className="mb-0 mt-1">
                    {errors.passwordStrength.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </small>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Confirm New Password *</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                isInvalid={!!errors.confirm}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.confirm}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={loading}>
          Cancel (Change Later)
        </Button>
        <Button
          variant="warning"
          onClick={handleSubmit}
          disabled={loading || !newPassword || !confirmPassword}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            <>
              <FaKey className="me-2" />
              Change Password
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DefaultPasswordModal;